// <marquee-image> — behaviour layer for the marquee in the `marquee-image` section.
// The scroll itself is a CSS animation on `.mib__mq-wrap` (translateX, converted to vertical
// travel for the desktop strip by a rotated viewport). This script adds:
//   • build: the marquee-item blocks render as hidden `.mib-mq__source` nodes in the content
//     column (theme blocks render via {% content_for 'blocks' %} in this theme), so the two tracks
//     arrive empty; on connect we clone each authored item into the live track and the
//     aria-hidden+inert duplicate. With zero item sources the whole container is hidden.
//   • fill: clones the authored item list within each track (until it is at least as long as the
//     viewport's travel length) so the seamless 2-track loop never exposes an empty background
//     gap when a single authored copy is shorter than the band/strip.
//   • hover: smoothly slow down or pause (retimes the CSS animation via playbackRate)
//   • drag-to-scrub: pointer drag scrubs the animation timeline, with light release inertia;
//     it keeps working under prefers-reduced-motion because it is user-initiated. The pointer ->
//     timeline sign mirrors the CSS-resolved animation direction, so scrubbing always feels
//     aligned with the ambient motion (whichever of left/right/up/down was chosen per breakpoint).
//   • post-drag click suppression, so a real drag-release never fires an accidental navigation
//     on a linked item.
// It never animates layout — it only reads/sets the existing animation's playbackRate /
// currentTime, plus clones already-rendered item nodes (marked inert/aria-hidden) to pad tracks.

const FILL_MAX_ROUNDS = 24;

class MarqueeImage extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this._gen = (this._gen || 0) + 1;
    const gen = this._gen;

    // The section CSS keeps this element invisible (opacity 0) until [data-mib-ready] is set, so
    // the strip background never flashes as an empty colored band pre-build. It MUST be set on
    // every code path (incl. the zero-sources path, where the element gets hidden anyway) or the
    // band would be stuck invisible.
    const ready = () => this.setAttribute('data-mib-ready', '');

    this.wrap = this.querySelector('[data-mq-wrap]');
    this.viewport = this.querySelector('.mib__mq-viewport');
    this.tracks = Array.from(this.querySelectorAll('.mib__mq-track'));
    if (!this.wrap || this.tracks.length === 0) {
      ready();
      return;
    }

    // Build the two tracks from the block-rendered item sources. In this theme's block
    // architecture the marquee-item blocks render as hidden `.mib-mq__source` nodes in the sibling
    // content column (blocks render via {% content_for 'blocks' %}), and these tracks arrive empty.
    // Returns false when there are no item sources — the container is then hidden and we stop.
    if (!this._buildTracks()) {
      ready(); // [hidden] wins anyway; keeps the attr contract intact if sources appear later
      return;
    }

    this.hoverMode = this.dataset.hover || 'none'; // none | slow | pause
    this.dragEnabled = this.dataset.drag === 'true';
    this.slowFactor = 0.28;

    this._reducedMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._reduced = this._reducedMedia.matches;
    this._reducedMedia.addEventListener?.('change', this._onReducedChange);

    this._setupFill();
    ready(); // tracks built + filled — reveal the strip

    // Grab the CSS animation once it exists (it may not on the very first frame).
    this._withAnimation((anim) => {
      // stale — the element was disconnected/reconnected (or re-init'd) while we were waiting
      if (gen !== this._gen || !this.isConnected) return;
      this.anim = anim;
      if (!this._reduced && this.hoverMode === 'slow') this._bindHover();
      if (this.dragEnabled) this._bindDrag();
    });
  }

  disconnectedCallback() {
    // let a future reconnect (theme-editor block reorder/move) run connectedCallback again
    this._init = false;
    this._reducedMedia?.removeEventListener?.('change', this._onReducedChange);
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._inertiaRaf) cancelAnimationFrame(this._inertiaRaf);
    if (this._fillRaf) cancelAnimationFrame(this._fillRaf);
    this._fillRO?.disconnect();
    this._fillRO = null;
    if (this._onWinResize) window.removeEventListener('resize', this._onWinResize);
    this._unbindHover();
    this._unbindDrag();
  }

  _onReducedChange = (e) => { this._reduced = e.matches; };

  // ---------- build: populate the two tracks from the block-rendered item sources ----------
  // The marquee-item blocks render as hidden `.mib-mq__source` wrappers in the content column
  // (theme blocks only render via {% content_for 'blocks' %} here), each carrying one authored
  // `.mib-mq__item`. Clone that item set into the live (announced) track and the aria-hidden+inert
  // duplicate; the seamless-loop fill and all scroll/hover/drag logic then run on the built tracks.
  // The hidden sources stay in the DOM (out of the a11y tree) so the theme editor keeps its anchor.
  _buildTracks() {
    const [live, dup] = this.tracks;
    if (!live || !dup) return false;

    // clear any prior build — a theme-editor block move can disconnect/reconnect this element
    live.replaceChildren();
    dup.replaceChildren();
    this._baseLive = null;
    this._baseDup = null;

    // sources live in the sibling content column, not inside this element — scope to the section
    const root = this.closest('.mib') || this.getRootNode();
    const items = Array.from(root.querySelectorAll('.mib-mq__source'))
      .map((src) => src.querySelector('.mib-mq__item'))
      .filter(Boolean);

    if (items.length === 0) {
      this.hidden = true; // no authored items — nothing to scroll
      return false;
    }
    this.hidden = false;

    items.forEach((item) => {
      // live copy: fully interactive (a linked item keeps its tab stop)
      live.appendChild(item.cloneNode(true));
      // duplicate copy: its track is already aria-hidden + inert; also drop any link tab stop
      const clone = item.cloneNode(true);
      if (clone.tagName === 'A') clone.tabIndex = -1;
      dup.appendChild(clone);
    });

    return true;
  }

  _withAnimation(cb, tries = 0) {
    if (!this.isConnected) return; // disconnected while polling — stop; CSS still runs the loop
    const found = this.wrap.getAnimations()[0];
    if (found) {
      cb(found);
      return;
    }
    if (tries > 30) return; // give up gracefully — CSS still runs the loop
    requestAnimationFrame(() => this._withAnimation(cb, tries + 1));
  }

  // ---------- fill: guarantee one track copy is >= the container's travel length ----------
  // Two identical tracks are required for the seamless -50% translate math; if a single authored
  // copy is narrower/shorter than the viewport, the empty strip-background sweeps across on every
  // loop restart. Clone the authored item list (marked inert/aria-hidden so it never duplicates
  // announced content or tab stops) into both tracks, equally, until that stops happening.
  _setupFill() {
    this._sizeVertical();
    this._fill();
    if (typeof ResizeObserver === 'function') {
      // observe only the strip box; the viewport size is set by _sizeVertical(), so observing
      // it too would loop. A grid-row / section resize changes the strip box and refires this.
      this._fillRO = new ResizeObserver(() => this._scheduleFill());
      this._fillRO.observe(this);
    } else {
      this._onWinResize = () => this._scheduleFill();
      window.addEventListener('resize', this._onWinResize);
    }
    // web fonts swapping in can change item widths after first paint
    document.fonts?.ready?.then(() => this._scheduleFill()).catch(() => {});
  }

  _scheduleFill() {
    if (this._fillRaf) cancelAnimationFrame(this._fillRaf);
    this._fillRaf = requestAnimationFrame(() => { this._sizeVertical(); this._fill(); });
  }

  // Desktop vertical strip: the viewport is rotated -90deg, so its pre-rotation width must equal
  // the strip's HEIGHT (the vertical travel length) and its height the strip's thickness, so after
  // rotation it fills the narrow tall strip. Sized from the real box here because the CSS
  // container-query approach collapsed the abspos strip's height to 0.
  _sizeVertical() {
    const vp = this.viewport;
    if (!vp) return;
    const desktopVertical =
      this.dataset.desktopVertical === 'true' && window.matchMedia('(min-width: 750px)').matches;
    if (!desktopVertical) {
      vp.style.width = '';
      vp.style.height = '';
      return;
    }
    const h = this.clientHeight; // full grid-row height (strip is inset-block:0)
    const w = this.clientWidth; // strip thickness
    if (h > 1 && w > 1) {
      vp.style.width = h + 'px';
      vp.style.height = w + 'px';
    }
  }

  _fill() {
    if (!this.viewport || this.tracks.length < 2) return;
    // offsetWidth is the pre-transform (local) length — correct even when the desktop strip's
    // viewport is rotated, since it is set via container-query units before the rotate() runs.
    const target = this.viewport.offsetWidth;
    const [live, dup] = this.tracks;
    if (!live || !dup || !target) return;

    if (!this._baseLive) {
      this._baseLive = Array.from(live.children).filter((el) => !el.hasAttribute('data-mib-filler'));
      this._baseDup = Array.from(dup.children).filter((el) => !el.hasAttribute('data-mib-filler'));
    }
    if (this._baseLive.length === 0) return;

    live.querySelectorAll('[data-mib-filler]').forEach((el) => el.remove());
    dup.querySelectorAll('[data-mib-filler]').forEach((el) => el.remove());

    // compute the needed round count from the live track, then mirror the exact same count onto
    // the duplicate track so both copies stay pixel-equal (required for the seam math to hold)
    let rounds = 0;
    while (live.offsetWidth < target && rounds < FILL_MAX_ROUNDS) {
      this._appendFillerRound(live, this._baseLive);
      rounds += 1;
    }
    for (let i = 0; i < rounds; i++) {
      this._appendFillerRound(dup, this._baseDup);
    }
  }

  _appendFillerRound(track, baseItems) {
    baseItems.forEach((el) => {
      const clone = el.cloneNode(true);
      // purely decorative padding — must never be reachable by assistive tech or keyboard
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      if (clone.tagName === 'A') clone.tabIndex = -1;
      clone.setAttribute('data-mib-filler', '');
      track.appendChild(clone);
    });
  }

  // ---------- hover: smoothly ramp playbackRate ----------
  _bindHover() {
    this.addEventListener('pointerenter', this._onPointerEnter);
    this.addEventListener('pointerleave', this._onPointerLeave);
    this.addEventListener('focusin', this._onFocusIn);
    this.addEventListener('focusout', this._onFocusOut);
  }

  _unbindHover() {
    this.removeEventListener('pointerenter', this._onPointerEnter);
    this.removeEventListener('pointerleave', this._onPointerLeave);
    this.removeEventListener('focusin', this._onFocusIn);
    this.removeEventListener('focusout', this._onFocusOut);
  }

  _onPointerEnter = (e) => {
    if (e.pointerType === 'touch' || this._dragging) return;
    this._ramp(this.slowFactor);
  };
  _onPointerLeave = () => {
    if (this._dragging) return;
    this._ramp(1);
  };
  _onFocusIn = () => this._ramp(this.slowFactor);
  _onFocusOut = () => { if (!this._dragging) this._ramp(1); };

  _ramp(target) {
    if (!this.anim) return;
    if (this._raf) cancelAnimationFrame(this._raf);
    const start = typeof this.anim.playbackRate === 'number' ? this.anim.playbackRate : 1;
    if (Math.abs(start - target) < 0.01) {
      this._setRate(target);
      return;
    }
    const dur = 320;
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min((now - t0) / dur, 1);
      const eased = p * p * (3 - 2 * p);
      this._setRate(start + (target - start) * eased);
      if (p < 1) this._raf = requestAnimationFrame(step);
    };
    this._raf = requestAnimationFrame(step);
  }

  _setRate(rate) {
    if (!this.anim) return;
    try {
      this.anim.playbackRate = rate;
    } catch (_) {
      /* no-op */
    }
  }

  // ---------- drag to scrub ----------
  _bindDrag() {
    this.style.touchAction = 'pan-y';
    this.wrap.style.cursor = 'grab';

    this.addEventListener('pointerdown', this._onDown);
    this.addEventListener('pointermove', this._onMove);
    this.addEventListener('pointerup', this._onUp);
    this.addEventListener('pointercancel', this._onUp);
    // capturing so a post-drag click on a nested <a> can be cancelled before it navigates
    this.addEventListener('click', this._onClickCapture, true);
  }

  _unbindDrag() {
    this.removeEventListener('pointerdown', this._onDown);
    this.removeEventListener('pointermove', this._onMove);
    this.removeEventListener('pointerup', this._onUp);
    this.removeEventListener('pointercancel', this._onUp);
    this.removeEventListener('click', this._onClickCapture, true);
  }

  // Orientation is resolved per breakpoint (desktop vs mobile can differ). Vertical means the
  // viewport is rotated -90deg for this breakpoint, so drag must read clientY instead of clientX.
  get _isVertical() {
    return window.matchMedia('(min-width: 750px)').matches
      ? this.dataset.desktopVertical === 'true'
      : this.dataset.mobileVertical === 'true';
  }

  // +1 for normal, -1 for reverse — mirrors the CSS-resolved animation-direction (which encodes
  // the chosen per-breakpoint direction, left/right/up/down) so drag always feels aligned with the
  // ambient motion instead of inverted.
  _dirSign() {
    if (!this.wrap) return 1;
    return getComputedStyle(this.wrap).animationDirection === 'reverse' ? -1 : 1;
  }

  // pixels-per-millisecond mapping for the current layout, measured on demand
  _measure() {
    const track = this.tracks[0];
    // offset* is the pre-transform (local) length — correct even when the viewport is rotated
    const period = (track.offsetWidth || 1) + this._gapPx();
    const timing = this.anim && this.anim.effect ? this.anim.effect.getComputedTiming() : null;
    const durMs = timing && typeof timing.duration === 'number' ? timing.duration : 20000;
    this._msPerPx = durMs / period;
    this._durMs = durMs;
  }

  _gapPx() {
    const cs = getComputedStyle(this.wrap);
    const g = parseFloat(cs.columnGap || cs.gap || '0');
    return isNaN(g) ? 0 : g;
  }

  _onDown = (e) => {
    if (!this.anim || e.button != null && e.button !== 0) return;
    // let real interactions with links happen unless the user actually drags
    this._measure();
    this._sign = this._dirSign();
    this._dragging = true;
    this._moved = 0;
    this._startPos = this._isVertical ? e.clientY : e.clientX;
    this._lastPos = this._startPos;
    this._lastT = performance.now();
    this._vel = 0; // px/ms
    this._baseTime = this._currentTime();
    if (this._inertiaRaf) cancelAnimationFrame(this._inertiaRaf);
    this._pause();
    this.wrap.classList.add('mib--dragging');
    this.wrap.style.willChange = 'transform';
    try { this.setPointerCapture(e.pointerId); } catch (_) {}
  };

  _onMove = (e) => {
    if (!this._dragging) return;
    const pos = this._isVertical ? e.clientY : e.clientX;
    const delta = pos - this._startPos;
    this._moved = Math.abs(delta);

    const now = performance.now();
    const dt = Math.max(now - this._lastT, 1);
    this._vel = (pos - this._lastPos) / dt;
    this._lastPos = pos;
    this._lastT = now;

    // dragging toward the natural travel direction advances the timeline
    this._scrub(this._baseTime - delta * this._msPerPx * this._sign);
    if (this._moved > 6) e.preventDefault();
  };

  _onUp = (e) => {
    if (!this._dragging) return;
    this._dragging = false;
    try { this.releasePointerCapture(e.pointerId); } catch (_) {}
    this.wrap.classList.remove('mib--dragging');

    // if it was effectively a tap on a link, don't hijack it
    if (this._moved < 6) {
      this._resume();
      this.wrap.style.willChange = '';
      return;
    }

    // a real drag happened — the browser is about to fire a click on release; suppress it so a
    // linked item doesn't get accidentally navigated.
    this._armClickSuppression();

    // light inertia, then resume the auto-scroll (unless reduced motion)
    let v = this._vel * -this._msPerPx * this._sign; // ms of timeline per ms of real time
    const decay = 0.92;
    const stepInertia = () => {
      v *= decay;
      this._scrub(this._currentTime() + v * 16);
      if (Math.abs(v) > 0.02) {
        this._inertiaRaf = requestAnimationFrame(stepInertia);
      } else {
        this._resume();
        this.wrap.style.willChange = '';
      }
    };
    this._inertiaRaf = requestAnimationFrame(stepInertia);
  };

  _armClickSuppression() {
    this._suppressClick = true;
    clearTimeout(this._suppressClickTimer);
    // safety net in case no click ever arrives (e.g. pointer released off-element)
    this._suppressClickTimer = setTimeout(() => { this._suppressClick = false; }, 400);
  }

  _onClickCapture = (e) => {
    if (!this._suppressClick) return;
    this._suppressClick = false;
    clearTimeout(this._suppressClickTimer);
    e.preventDefault();
    e.stopPropagation();
  };

  _currentTime() {
    const t = this.anim ? this.anim.currentTime : 0;
    return typeof t === 'number' ? t : 0;
  }

  _scrub(time) {
    if (!this.anim) return;
    const dur = this._durMs || 20000;
    // wrap into [0, dur) so an infinite loop scrubs seamlessly in both directions
    let t = time % dur;
    if (t < 0) t += dur;
    try {
      this.anim.currentTime = t;
    } catch (_) {}
  }

  _pause() {
    if (!this.anim) return;
    try { this.anim.pause(); } catch (_) {}
  }

  _resume() {
    if (!this.anim) return;
    // under reduced motion, keep it frozen (paused) — drag stays available
    if (this._reduced) return;
    // a still-hovering pointer in 'pause' mode must keep the marquee paused, matching the CSS
    // :hover rule — a drag-release must not override it and resume the animation underneath.
    if (this.hoverMode === 'pause' && this.matches(':hover')) return;
    this._setRate(this.hoverMode === 'slow' && this.matches(':hover') ? this.slowFactor : 1);
    try { this.anim.play(); } catch (_) {}
  }
}

if (!customElements.get('marquee-image')) {
  customElements.define('marquee-image', MarqueeImage);
}
